import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vModelText as _vModelText, normalizeClass as _normalizeClass, withDirectives as _withDirectives, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, resolveDirective as _resolveDirective } from "vue"

const _hoisted_1 = { class: "flex flex-col lg:flex-row lg:items-center" }
const _hoisted_2 = { class: "sr-only" }
const _hoisted_3 = { class: "relative flex-1" }
const _hoisted_4 = ["placeholder"]
const _hoisted_5 = {
  key: 1,
  class: "scrollbar-hide absolute left-0 right-0 top-full z-[2700] mt-2 max-h-60 overflow-y-auto rounded-lg border border-concrete-300 bg-white py-1 shadow-xl"
}
const _hoisted_6 = ["onClick"]

import { ref, watch, onMounted } from "vue";
import { storeToRefs } from "pinia";

import { debounce } from "~/js/composables/debounce";
import { useMapboxGeocoding, type GeocodingFeature } from "~/js/composables/mapbox-geocoding";
import { useLocationsStore } from "~/js/store/locations";

import SvgLoader from "~/vue/atoms/svgs/SvgLoader.vue";

interface Props {
	label: string;
	loading: boolean;
	utilities?: string;
	/** Borderless input for use inside the combined search pill. */
	embedded?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'FilterLocationSearch',
  props: {
    label: {},
    loading: { type: Boolean },
    utilities: { default: "" },
    embedded: { type: Boolean, default: false }
  },
  setup(__props: any) {



const locationsStore = useLocationsStore();
const { searchPlace } = storeToRefs(locationsStore);
const { searchPlaces } = useMapboxGeocoding();

const query = ref("");
const suggestions = ref<GeocodingFeature[]>([]);
const suggestionsOpen = ref(false);
const searching = ref(false);

const syncFromStore = () => {
	query.value = searchPlace.value?.label ?? "";
};

onMounted(syncFromStore);

watch(
	searchPlace,
	() => {
		syncFromStore();
	},
	{ deep: true },
);

const fetchSuggestions = debounce(async (value: string) => {
	if (!value.trim()) {
		suggestions.value = [];
		searching.value = false;
		return;
	}

	searching.value = true;
	suggestions.value = await searchPlaces(value);
	searching.value = false;
	suggestionsOpen.value = suggestions.value.length > 0;
});

const onInput = () => {
	suggestionsOpen.value = false;
	fetchSuggestions(query.value);
};

const selectPlace = (feature: GeocodingFeature) => {
	query.value = feature.place_name;
	suggestions.value = [];
	suggestionsOpen.value = false;

	locationsStore.setSearchPlace({
		label: feature.place_name,
		latitude: feature.center[1],
		longitude: feature.center[0],
		placeId: feature.id,
	});
};

const clear = () => {
	query.value = "";
	suggestions.value = [];
	suggestionsOpen.value = false;
	locationsStore.setSearchPlace(null);
};

const closeSuggestions = () => {
	suggestionsOpen.value = false;
};

return (_ctx: any,_cache: any) => {
  const _directive_click_away = _resolveDirective("click-away")!

  return _withDirectives((_openBlock(), _createElementBlock("fieldset", {
    class: _normalizeClass(['relative min-w-0', suggestionsOpen.value ? 'z-[2600]' : '', __props.utilities])
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("legend", _hoisted_2, _toDisplayString(__props.label), 1),
      _createElementVNode("div", _hoisted_3, [
        _withDirectives(_createElementVNode("input", {
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((query).value = $event)),
          type: "search",
          autocomplete: "off",
          class: _normalizeClass([
						'inline-flex w-full items-center py-2 print:hidden',
						__props.embedded
							? 'rounded-full border border-concrete-500 bg-white pl-4 pr-10 shadow-sm'
							: 'rounded-full border border-concrete-500 bg-white pl-3 pr-10 shadow-sm',
						'font-primary text-sm text-black',
						'focus:outline-none focus:ring-4 focus:ring-celestial-500',
					]),
          placeholder: __props.label,
          onInput: onInput,
          onFocus: _cache[1] || (_cache[1] = ($event: any) => (suggestionsOpen.value = suggestions.value.length > 0))
        }, null, 42, _hoisted_4), [
          [_vModelText, query.value]
        ]),
        _createElementVNode("span", {
          class: _normalizeClass([
						query.value.length === 0 && !__props.loading && !searching.value ? 'opacity-100' : 'opacity-0',
						'pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-black',
					])
        }, [...(_cache[2] || (_cache[2] = [
          _createElementVNode("i", { class: "fas fa-magnifying-glass" }, null, -1)
        ]))], 2),
        _createElementVNode("span", {
          class: _normalizeClass([
						__props.loading || searching.value ? 'opacity-100' : 'opacity-0',
						'pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-viridian-500',
					])
        }, [
          _createVNode(SvgLoader)
        ], 2),
        (query.value.length > 0 && !__props.loading && !searching.value)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              type: "button",
              class: "absolute right-4 top-1/2 -translate-y-1/2 text-viridian-500",
              onClick: clear
            }, [...(_cache[3] || (_cache[3] = [
              _createElementVNode("i", { class: "far fa-times" }, null, -1)
            ]))]))
          : _createCommentVNode("", true),
        (suggestionsOpen.value && suggestions.value.length > 0)
          ? (_openBlock(), _createElementBlock("ul", _hoisted_5, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(suggestions.value, (feature) => {
                return (_openBlock(), _createElementBlock("li", {
                  key: feature.id
                }, [
                  _createElementVNode("button", {
                    type: "button",
                    class: "inline-flex w-full px-4 py-2 text-left font-primary text-sm text-black hover:bg-concrete-200",
                    onClick: ($event: any) => (selectPlace(feature))
                  }, _toDisplayString(feature.place_name), 9, _hoisted_6)
                ]))
              }), 128))
            ]))
          : _createCommentVNode("", true)
      ])
    ])
  ], 2)), [
    [_directive_click_away, closeSuggestions]
  ])
}
}

})